import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  Typography
} from "@material-ui/core";
import {
  CommonDialog,
  FilePreview,
  DragAndDropFiles,
  Select 
} from "components";
import { FileUploader } from "react-drag-drop-files";
import { GROUP_SERVICES } from "Services";
import { Context } from "context";
import { ADD_NOTIFICATION } from 'context/actions';
import styles from "./UploadMaterialModal.module.scss";

const MATERIAL_TYPE_OPTIONS = [
  { value: "playbook", label: "Playbook" },
  { value: "bifold", label: "Bifold" },
  { value: "poster", label: "Poster" },
  { value: "other", label: "Other" }
];

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" }
];

const UploadMaterialModal = (props) => {
  const {
    handleClose,
    groupId,
    onUpdate,
    existingMaterials = [],
    editingMaterial = null
  } = props;

  const isEditing = Boolean(editingMaterial);

  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(editingMaterial?.name || "");
  const [sizeError, setSizeError] = useState(false);
  const [typeError, setTypeError] = useState(false);
  const [materialType, setMaterialType] = useState(editingMaterial?.materialType || "");
  const [language, setLanguage] = useState(editingMaterial?.language || "en");

  const { dispatch } = useContext(Context);

  const fileTypes = ["PDF"];
  const maxFileSize = 100;

  const setGlobalNotification = (severity, message) =>
    dispatch({
      type: ADD_NOTIFICATION,
      payload: {
        notification: {
          severity,
          message
        }
      }
    });

  const handleFileChange = (cFile) => setFile(cFile);

  const handleFilenameChange = (e) => setFileName(e.target.value);

  const onRemove = () => {
    setFile(null);
    setSizeError(false);
    setTypeError(false);
  };

  const closeModal = () => {
    onRemove();
    handleClose();
  };

  const renderSelectField = (id, label, value, onChange, options, required = false) => (
    <FormControl fullWidth required={required} variant="outlined">
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        id={id}
        labelId={`${id}-label`}
        label={required ? `${label}*` : label}
        value={value}
        onChange={onChange}
      >
        {options.map(({ value: optionValue, label: optionLabel }) => (
          <MenuItem key={optionValue} value={optionValue}>{optionLabel}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // Any other material (excluding the one being edited, if any) whose
  // trimmed, case-insensitive name matches the current Document Title.
  const duplicateMaterial = existingMaterials.find(
    (m) =>
      m.id !== editingMaterial?.id &&
      (m.name || '').trim().toLowerCase() === (fileName || '').trim().toLowerCase()
  );
  const isDuplicateName = Boolean((fileName || '').trim()) && Boolean(duplicateMaterial);

  // Edit mode only: the subset of name/material_type/language that
  // actually differ from the material being edited -- a true PATCH sends
  // only what changed, not the current value of every field.
  const changedFields = isEditing
    ? {
        ...(fileName !== editingMaterial.name && { name: fileName }),
        ...(materialType !== editingMaterial.materialType && { material_type: materialType }),
        ...(language !== editingMaterial.language && { language: language })
      }
    : {};

  const hasChanges = Object.keys(changedFields).length > 0 || Boolean(file);

  // Uploads the picked file to S3 via the pre-signed link flow and returns
  // the {filename, key} pair the backend needs, or {} if no new file was
  // picked (edit mode, keeping the existing file).
  const uploadPickedFile = async () => {
    if (!file) return {};

    const uploadLink = await GROUP_SERVICES.createFileUploadLink();
    const fields = uploadLink?.data?.fields;
    const fdata = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      fdata.set(key, value);
    });
    fdata.set('file', file);
    const uploadResponse = await GROUP_SERVICES.uploadFile(uploadLink?.data?.url, fdata);
    if (uploadResponse?.type !== 'success') {
      throw "Error in uploading marketing material";
    }
    return { filename: file.name, key: fields?.key };
  };

  const handleUpload = async () => {
    if (!isEditing && !file) return;

    setLoading(true);
    try {
      const filePayload = await uploadPickedFile();

      let response;
      if (isEditing) {
        // True PATCH -- only fields that actually changed, plus the file
        // pair if a new one was picked.
        response = await GROUP_SERVICES.updateMarketingMaterial(editingMaterial.id, {
          ...changedFields,
          ...filePayload
        });
      } else {
        const createPayload = {
          level: 'group',
          level_id: groupId,
          name: fileName,
          material_type: materialType,
          language: language,
          ...filePayload
        };

        response = await GROUP_SERVICES.insertMarketingMaterial(createPayload);
      }

      if (response?.type === 'success') {
        setGlobalNotification("Success", `Marketing material ${isEditing ? 'updated' : 'uploaded'} successfully`);
        handleClose();
        onUpdate();
      } else {
        throw response?.message || response?.error;
      }
    } catch (err) {
      setGlobalNotification("error", err || `Error ${isEditing ? 'updating' : 'uploading'} marketing material`);
    }
    setLoading(false);
  };

  return (
    <CommonDialog
      open={true}
      dialogTitle={isEditing ? "Edit Marketing Material" : "Upload Marketing Material"}
      primaryBtnTxt={isEditing ? "Save Changes" : "Upload File"}
      handlePrimaryButtonClick={handleUpload}
      disabledPrimaryButton={loading || !fileName || !materialType || (!isEditing && !file) || (isEditing && !hasChanges) || isDuplicateName}
      primaryButtonStyles={{ minWidth: 125 }}
      secondaryBtnTxt="Cancel"
      disableSecondaryButton={loading}
      handleClose={closeModal}
      maxWidth='sm'
      isLoading={loading}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}> </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="file_name"
                    value={fileName}
                    label="Document Title"
                    variant="outlined"
                    onChange={(e) => handleFilenameChange(e)}
                    error={isDuplicateName}
                    helperText={isDuplicateName ? 'A material with this name already exists. Please choose a different name.' : undefined}
                    inputProps={{
                      'data-testid': 'file-name'
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  {renderSelectField(
                    'material-type-select',
                    'Category',
                    materialType,
                    (e) => setMaterialType(e.target.value),
                    MATERIAL_TYPE_OPTIONS,
                    true
                  )}
                </Grid>
                <Grid item xs={12}>
                  {renderSelectField(
                    'material-language-select',
                    'Language',
                    language,
                    (e) => setLanguage(e.target.value),
                    LANGUAGE_OPTIONS
                  )}
                </Grid>
                <Grid item xs={12}>
                  {file && !sizeError && !typeError && (
                    <FilePreview
                      name={file.name}
                      size={file.size}
                      onRemove={onRemove}
                    />
                  )}

                  {!file && (
                    <>
                      {isEditing && (
                        <div className={styles.current_file}>
                          <Typography variant="body2" color="textSecondary">
                            Current file: {editingMaterial?.name || 'existing file'}. Upload a file below to replace it, or leave as-is to keep it.
                          </Typography>
                        </div>
                      )}
                      <FileUploader
                        handleChange={handleFileChange}
                        name="file"
                        types={fileTypes}
                        children={<DragAndDropFiles maxSize={maxFileSize}/>}
                        maxSize={maxFileSize}
                        minSize={0}
                        onSizeError={() => setSizeError(true)}
                        onTypeError={() => setTypeError(true)}
                      />
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              {sizeError && (
                <Typography className={styles.texterror}>Upload Failed. Please upload a file less than {maxFileSize}MB.</Typography>
              )}

              {typeError && (
                <Typography className={styles.texterror}>Upload Failed. Please upload a file of type PDF.</Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </CommonDialog>
  );
}

UploadMaterialModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  existingMaterials: PropTypes.array,
  editingMaterial: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    materialType: PropTypes.string,
    language: PropTypes.string
  })
};

export default UploadMaterialModal;
